import { AlertTriangle, KeyRound, Loader2, RefreshCw, Shield, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { authorizationService } from "@/lib/services";
import type { Authorization } from "@/types";

const PURPOSE_LABELS: Record<string, string> = {
  data_sync: "Automated syncs",
  scheduled_flows: "Scheduled workflows",
  micro_app_data: "Shared dashboard live data",
};

function purposeLabel(purpose: string): string {
  return PURPOSE_LABELS[purpose] ?? purpose;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function SettingsAuthorizationsTab() {
  const [authorizations, setAuthorizations] = useState<Authorization[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authorizationService.list();
      setAuthorizations(res.data);
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to load authorizations", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRevoke = async (auth: Authorization) => {
    const confirmed = await confirm({
      title: "Revoke authorization",
      message: `This immediately removes Anthm's saved permission for "${purposeLabel(
        auth.purpose,
      )}" and stops any background operations that rely on it. Continue?`,
      confirmText: "Revoke",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      setRevoking(auth.purpose);
      await authorizationService.revoke(auth.purpose);
      showToast("Authorization revoked", "success");
      await load();
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to revoke authorization", "error");
    } finally {
      setRevoking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Authorizations
        </h2>
        <p className="text-sm text-content-tertiary mt-1">
          Background access you've granted Anthm to act on your behalf using your Microsoft permissions. Anthm can only
          access what you can already access. Revoke any of these at any time.
        </p>
      </div>

      {authorizations.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-12 bg-surface-primary border border-border-primary rounded-xl">
          <KeyRound className="w-12 h-12 text-content-quaternary" />
          <p className="text-content-tertiary mt-3">You have no active authorizations.</p>
          <p className="text-content-quaternary text-sm mt-1">
            Enable automated sync on a SharePoint data source to authorize background access.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {authorizations.map((auth) => (
            <AuthorizationCard
              key={auth.uuid}
              auth={auth}
              revoking={revoking === auth.purpose}
              onRevoke={() => handleRevoke(auth)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AuthorizationCard({
  auth,
  revoking,
  onRevoke,
}: Readonly<{ auth: Authorization; revoking: boolean; onRevoke: () => void }>) {
  const needsReauth = auth.syncTargets.some((t) => t.reauthorizationRequired) || !auth.isValid;

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-content-primary">{purposeLabel(auth.purpose)}</h3>
            {needsReauth ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5" />
                Re-authorization required
              </span>
            ) : (
              <span className="text-xs font-medium text-green-400">Active</span>
            )}
          </div>
          <p className="text-xs text-content-tertiary mt-1">
            Authorized {formatDate(auth.authorizedAt)} · Last used {formatDate(auth.lastUsedAt)}
          </p>
        </div>
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-400 hover:bg-interactive-hover rounded-lg disabled:opacity-50"
        >
          {revoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          Revoke
        </button>
      </div>

      {auth.syncTargets.length > 0 && (
        <div className="mt-4 border-t border-border-secondary pt-4">
          <p className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-2">Data sources</p>
          <ul className="space-y-2">
            {auth.syncTargets.map((target) => (
              <li key={target.uuid} className="flex items-center justify-between text-sm">
                <span className="text-content-secondary truncate">{target.name}</span>
                <span className="flex items-center gap-3 text-content-tertiary shrink-0">
                  {target.reauthorizationRequired && (
                    <span className="inline-flex items-center gap-1 text-amber-400">
                      <RefreshCw className="w-3.5 h-3.5" />
                      Re-authorize
                    </span>
                  )}
                  <span>Last sync {formatDate(target.lastSyncedAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
